/**
 * Storage Backup & Restore Utilities
 *
 * Provides backup, restore, and export functionality for localStorage data.
 * Useful for protecting user data during development.
 */

interface BackupData {
  objects: string | null;
  recordPages: string | null;
  pageLayouts: string | null;
  objectFields: string | null;
  workflows: string | null;
  roles: string | null;
  users: string | null;
  fieldPermissions: string | null;
  emailTemplates: string | null;
  timestamp: string;
}

/**
 * Create a backup of all CRM localStorage data
 */
export const backupStorage = (): BackupData => {
  const backup: BackupData = {
    objects: localStorage.getItem('crm_objects'),
    recordPages: localStorage.getItem('crm_record_pages'),
    pageLayouts: localStorage.getItem('crm_page_layouts'),
    objectFields: localStorage.getItem('crm_object_fields'),
    workflows: localStorage.getItem('crm_workflows'),
    roles: localStorage.getItem('crm_roles'),
    users: localStorage.getItem('crm_users'),
    fieldPermissions: localStorage.getItem('crm_field_permissions'),
    emailTemplates: localStorage.getItem('crm_email_templates'),
    timestamp: new Date().toISOString()
  };

  localStorage.setItem('crm_backup', JSON.stringify(backup));
  console.log('💾 Storage backed up at', backup.timestamp);

  return backup;
};

/**
 * Restore from the last backup
 */
export const restoreStorage = (): boolean => {
  const backupStr = localStorage.getItem('crm_backup');
  if (!backupStr) {
    console.error('❌ No backup found');
    return false;
  }

  try {
    const backup: BackupData = JSON.parse(backupStr);

    // Restore each key if it exists in backup
    if (backup.objects) localStorage.setItem('crm_objects', backup.objects);
    if (backup.recordPages) localStorage.setItem('crm_record_pages', backup.recordPages);
    if (backup.pageLayouts) localStorage.setItem('crm_page_layouts', backup.pageLayouts);
    if (backup.objectFields) localStorage.setItem('crm_object_fields', backup.objectFields);
    if (backup.workflows) localStorage.setItem('crm_workflows', backup.workflows);
    if (backup.roles) localStorage.setItem('crm_roles', backup.roles);
    if (backup.users) localStorage.setItem('crm_users', backup.users);
    if (backup.fieldPermissions) localStorage.setItem('crm_field_permissions', backup.fieldPermissions);
    if (backup.emailTemplates) localStorage.setItem('crm_email_templates', backup.emailTemplates);

    console.log('✅ Storage restored from', backup.timestamp);
    return true;
  } catch (error) {
    console.error('❌ Failed to restore backup:', error);
    return false;
  }
};

/**
 * Export storage data to a downloadable JSON file
 */
export const exportStorage = (): void => {
  const data: BackupData = {
    objects: localStorage.getItem('crm_objects'),
    recordPages: localStorage.getItem('crm_record_pages'),
    pageLayouts: localStorage.getItem('crm_page_layouts'),
    objectFields: localStorage.getItem('crm_object_fields'),
    workflows: localStorage.getItem('crm_workflows'),
    roles: localStorage.getItem('crm_roles'),
    users: localStorage.getItem('crm_users'),
    fieldPermissions: localStorage.getItem('crm_field_permissions'),
    emailTemplates: localStorage.getItem('crm_email_templates'),
    timestamp: new Date().toISOString()
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `crm-backup-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  console.log('💾 Storage exported to file');
};

/**
 * Import storage data from a JSON file
 */
export const importStorage = (jsonData: string): boolean => {
  try {
    const data: BackupData = JSON.parse(jsonData);

    // Restore each key if it exists
    if (data.objects) localStorage.setItem('crm_objects', data.objects);
    if (data.recordPages) localStorage.setItem('crm_record_pages', data.recordPages);
    if (data.pageLayouts) localStorage.setItem('crm_page_layouts', data.pageLayouts);
    if (data.objectFields) localStorage.setItem('crm_object_fields', data.objectFields);
    if (data.workflows) localStorage.setItem('crm_workflows', data.workflows);
    if (data.roles) localStorage.setItem('crm_roles', data.roles);
    if (data.users) localStorage.setItem('crm_users', data.users);
    if (data.fieldPermissions) localStorage.setItem('crm_field_permissions', data.fieldPermissions);
    if (data.emailTemplates) localStorage.setItem('crm_email_templates', data.emailTemplates);

    console.log('✅ Storage imported from file (timestamp:', data.timestamp, ')');
    return true;
  } catch (error) {
    console.error('❌ Failed to import storage:', error);
    return false;
  }
};

/**
 * Show storage statistics
 */
export const showStorageStats = (): void => {
  console.log('📊 Storage Statistics:');

  const keys = [
    'crm_objects',
    'crm_record_pages',
    'crm_page_layouts',
    'crm_object_fields',
    'crm_workflows',
    'crm_roles',
    'crm_users',
    'crm_field_permissions',
    'crm_email_templates'
  ];

  keys.forEach(key => {
    const data = localStorage.getItem(key);
    if (data) {
      try {
        const parsed = JSON.parse(data);
        const count = Array.isArray(parsed) ? parsed.length : 'N/A';
        const size = (data.length / 1024).toFixed(2);
        console.log(`  ${key}: ${count} items, ${size} KB`);
      } catch {
        const size = (data.length / 1024).toFixed(2);
        console.log(`  ${key}: ${size} KB`);
      }
    } else {
      console.log(`  ${key}: (empty)`);
    }
  });

  // Check for backup
  const backup = localStorage.getItem('crm_backup');
  if (backup) {
    try {
      const parsed = JSON.parse(backup);
      console.log(`  📦 Backup available from: ${parsed.timestamp}`);
    } catch {
      console.log('  📦 Backup exists but corrupted');
    }
  } else {
    console.log('  ⚠️ No backup found');
  }
};

/**
 * Export utilities to window for easy console access
 */
if (typeof window !== 'undefined') {
  (window as any).crmBackup = backupStorage;
  (window as any).crmRestore = restoreStorage;
  (window as any).crmExport = exportStorage;
  (window as any).crmStats = showStorageStats;

  console.log('💡 Storage utilities available:');
  console.log('   window.crmBackup()  - Create backup');
  console.log('   window.crmRestore() - Restore from backup');
  console.log('   window.crmExport()  - Export to file');
  console.log('   window.crmStats()   - Show statistics');
}
